import * as React from 'react';

import moment from 'moment';

import type {Tab} from 'shells/control-bar/page-control-bar';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import {ChatBubbleRegularIcon} from 'shells/icon/svg/chat-bubble-regular-icon';
import {MicrophoneRegularIcon} from 'shells/icon/svg/microphone-regular-icon';
import {NoteRegularIcon} from 'shells/icon/svg/note-regular-icon';
import {PlayCircleRegularIcon} from 'shells/icon/svg/play-circle-regular-icon';
import {Link} from 'shells/link/link';
import {TextField} from 'shells/textfield/textfield';
import {TextfieldWithButtons} from 'shells/textfield/textfield-with-buttons';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';

import type {ExternalService} from 'nutshell-graphql-types';
import type {GetActivityForModal_activity as ActivityForModal} from 'nutshell-graphql-types';

import {writeLocalStorage, readLocalStorage} from '../../util/localStorage';

import {checkHasRecordings} from './tabs/recordings/helpers';
import type {PendingConfirmAction, ModalVariant} from './types';

import styles from './activity-modal.module.css';

const ORIGIN_TYPES_TO_HIDE = ['WEB', 'UNKNOWN'];

// Hide the year if the timestamp is in the current year
export function getFooterTimestamp(timestamp: number): React.ReactElement {
    const isCurrentYear = moment.unix(timestamp).isSame(moment(), 'year');

    return (
        <Body isBold={true} color='light' size='small'>
            <FormattedTimestamp
                datetime={timestamp}
                format={
                    isCurrentYear
                        ? FormattedTimestamp.formats.shortDayMonthWithAtTime
                        : FormattedTimestamp.formats.shortDayLongMonthYearWithAtTime
                }
                isInline={true}
            />
        </Body>
    );
}

export function getCreatorOrigin(
    activity: ActivityForModal,
    isPopover?: boolean
): React.ReactElement {
    return (
        <>
            {activity.creatorOrigin &&
                !ORIGIN_TYPES_TO_HIDE.includes(activity.creatorOrigin.originType) && (
                    <>
                        {' '}
                        via{' '}
                        {activity.originUrl ? (
                            <>
                                {isPopover ? (
                                    <Body color='white' size='small'>
                                        {activity.hasBooking
                                            ? `Scheduler`
                                            : activity.creatorOrigin.name}
                                    </Body>
                                ) : (
                                    <Link variant='primary' href={activity.originUrl} size='small'>
                                        {activity.hasBooking
                                            ? `Scheduler - ${activity.creatorOrigin.name}`
                                            : activity.creatorOrigin.name}
                                    </Link>
                                )}
                            </>
                        ) : (
                            <Body color='light' size='small'>
                                {activity.creatorOrigin.name}
                            </Body>
                        )}
                    </>
                )}
        </>
    );
}

export function getCalendarServiceName(
    calendarSyncExternalService?: ExternalService | null
): string {
    switch (calendarSyncExternalService) {
        case 'GOOGLE':
            return 'Google';
        case 'OFFICE365':
            return 'Microsoft';
        default:
            return '';
    }
}

export function getHeaderTitle(
    isNew: boolean,
    title: string,
    isEditingTitle: boolean,
    titleButtonStyleName: string,
    dataTip: string,
    handleTitleClick: () => void,
    handleChangeTitle: (newTitle: string) => void,
    handleStopEditing: () => void,
    handleSaveClicked: (newTitle: string) => void
): React.ReactElement {
    if (isEditingTitle) {
        if (isNew) {
            return (
                <div className={styles['modal-header-title-editable']}>
                    <TextField
                        value={title}
                        onChange={handleChangeTitle}
                        // @ts-expect-error
                        onBlur={handleStopEditing}
                        focusOnMount={true}
                        onEnter={handleStopEditing}
                        boldText={true}
                        bigText={true}
                    />
                </div>
            );
        } else {
            return (
                <div className={styles['modal-header-title-editable']}>
                    <TextfieldWithButtons
                        initialValue={title}
                        onSave={handleSaveClicked}
                        onCancel={handleStopEditing}
                        isSaving={!isEditingTitle}
                        name='title'
                        title='Title'
                        focusOnMount={true}
                        hideTitle={true}
                        flexDirection='row'
                        noPadding={true}
                        boldText={true}
                        bigText={true}
                    />
                </div>
            );
        }
    } else {
        return (
            <button
                onClick={handleTitleClick}
                data-tip={dataTip}
                data-multiline={true}
                data-delay-show='0'
                className={titleButtonStyleName}
            >
                <Body size='large' isBold={true} truncate={{type: 'inline'}}>
                    {title}
                </Body>
            </button>
        );
    }
}

export function refreshActivityState(shouldRefreshTimeline: boolean, activityId?: string) {
    if (shouldRefreshTimeline) {
        Timeline.refreshTimeline();
    }

    if (activityId) {
        Activity.refreshActivities(parseInt(activityId));
    }
}

export function getRelatedEntityIds(activity?: ActivityForModal | null): string[] {
    if (!activity) {
        return [];
    }

    const relatedEntityIds = [];
    if (activity.relatedLeads) {
        relatedEntityIds.push(...activity.relatedLeads.map((lead) => lead.id));
    }
    if (activity.participants) {
        relatedEntityIds.push(...activity.participants.map((participant) => participant.id));
    }

    return relatedEntityIds;
}

export function isModalCreating(variant: ModalVariant): boolean {
    return variant === 'create' || variant === 'log';
}

export function getStartingTabByVariant(variant: ModalVariant): string {
    switch (variant) {
        case 'create':
            return 'schedule';
        case 'update':
            return 'internal-notes';
        case 'log':
            return 'log';
        default:
            return 'schedule';
    }
}

export function getTabs(
    variant: ModalVariant,
    activity?: ActivityForModal | null,
    commentCount?: number | null
): Tab[] {
    const isCreating = isModalCreating(variant);

    return [
        {
            id: 'schedule',
            label: 'Schedule',
            icon: CalendarSquareRegularActivityIcon,
            isDisabled: !isCreating,
            isHidden: variant !== 'create',
        },
        {
            id: 'log',
            label: 'Log',
            icon: CalendarSquareRegularActivityIcon,
            isDisabled: !isCreating,
            isHidden: variant !== 'log',
        },
        {
            id: 'internal-notes',
            label: 'Internal notes',
            icon: NoteRegularIcon,
            isDisabled: isCreating,
            isHidden: false,
        },
        {
            id: 'calendar',
            label: 'Calendar',
            icon: CalendarSquareRegularActivityIcon,
            isDisabled: isCreating,
            isHidden: false,
        },
        {
            id: 'transcript',
            label: 'Transcript',
            icon: MicrophoneRegularIcon,
            isDisabled: isCreating,
            isHidden: isCreating,
            showCheckmark: Boolean(
                activity?.transcription &&
                    activity?.canViewSummaryDetails &&
                    !activity?.summaryStates.isNotetakerDisabled
            ),
        },
        {
            id: 'recordings',
            label: 'Recordings',
            icon: PlayCircleRegularIcon,
            isDisabled: isCreating,
            isHidden: false,
            showCheckmark: Boolean(
                activity && checkHasRecordings(activity) && activity.canViewSummaryDetails
            ),
        },
        {
            id: 'comments',
            label: 'Comments',
            icon: ChatBubbleRegularIcon,
            isDisabled: isCreating,
            isHidden: false,
            count: commentCount ? commentCount.toString() : undefined,
        },
    ];
}

export function getUnsavedChangesMessage(pendingConfirmAction: PendingConfirmAction): string {
    const defaultMessage = 'Are you sure you want to close? Any unsaved changes will be lost.';

    if (!pendingConfirmAction || pendingConfirmAction.type === 'close') {
        return defaultMessage;
    } else if (pendingConfirmAction.type === 'changeTab') {
        return 'Are you sure you want to change tabs? Any unsaved changes will be lost.';
    } else {
        return defaultMessage;
    }
}

const SHOULD_SYNC_TO_CALENDAR_KEY = 'shouldSyncNewActivitiesToCalendar';
export function getOrCreateLocalStorageShouldSyncToCalendar(): boolean {
    const shouldSyncToCalendar = readLocalStorage(SHOULD_SYNC_TO_CALENDAR_KEY);
    if (shouldSyncToCalendar === null) {
        setLocalStorageShouldSyncToCalendar(false);

        return false;
    }

    return shouldSyncToCalendar === 'true';
}

export function setLocalStorageShouldSyncToCalendar(shouldSync: boolean) {
    writeLocalStorage(SHOULD_SYNC_TO_CALENDAR_KEY, shouldSync ? 'true' : 'false');
}
